<script lang="ts" setup>
import { computed, onMounted, ref, type HTMLAttributes, type ImgHTMLAttributes } from "vue";
import { cn } from "@/lib/utils";

export interface ImageProps extends /* @vue-ignore */ ImgHTMLAttributes {
	class?: HTMLAttributes["class"];
	src: string;
	alt: string;
	width: string;
	height: string;
	fetchpriority?: "high" | "low" | "auto";
}

const props = defineProps<ImageProps>();

const loading = ref<boolean>(true);
const visible = ref<boolean>(true);
const error = ref<boolean>(false);

function onImageLoad() {
	loading.value = false;
}

function onImageError() {
	loading.value = false;
	visible.value = false;
	error.value = true;
}

const roundedClass = computed(() => {
	const classStr = typeof props.class === "string" ? props.class : "";
	const classList = classStr.split(" ");
	return classList.filter((cls: string) => cls.startsWith("rounded")).join(" ");
});

onMounted(() => {
	setTimeout(() => {
		if (loading.value) {
			visible.value = false;
		}
	}, 15000);
});
</script>

<template>
    <div v-if="visible" class="relative" :class="`w-[${props.width}px] h-[${props.height}px] flex items-center`">
        <Skeleton v-if="loading" :class="`w-full h-full absolute ${roundedClass}`">
            <ImageSkeletonSvg :alt="props.alt" :width="props.width" :height="props.height" />
        </Skeleton>
        <img v-if="!error" :src="props.src" :alt="props.alt" :width="props.width" :height="props.height"
            :class="cn('object-cover', props.class)" data-testid="Image"
            @load="onImageLoad" @error="onImageError" loading="eager" :fetchpriority="props.fetchpriority" />
        <div v-else class="w-full h-full flex items-center justify-center bg-gray-200">
            <ImageSkeletonSvg :alt="props.alt" :width="props.width" :height="props.height"
                :class="`w-[${props.width}px] h-[${props.height}px]`" />
        </div>
    </div>
    <div v-if="error" :class="`w-[${props.width}px] h-[${props.height}px] flex items-center justify-center`">
        <ImageSkeletonSvg :alt="props.alt" :width="props.width" :height="props.height"
            :class="`w-[${props.width}px] h-[${props.height}px]`" />
    </div>
</template>
